// TanStack hooks for the image-update surface (Infrastructure > Updates).
//
// Registry tag listings are rate-limited and only change when a release
// ships, so the check queries use a long staleTime; the Updates page
// offers an explicit "Check now" that invalidates. Mutations return an
// operationId — callers navigate to /operations/:id where the step
// timeline + SSE log follow the multi-minute roll.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, unwrap } from "@/lib/api";

/** Long staleTime: a registry check is only stale when a release ships. */
const CHECK_STALE_MS = 30 * 60_000;

export interface SelfUpdateCheck {
	current: string;
	latest: string | null;
	updateAvailable: boolean;
	channel: string;
	error?: string;
}

export interface SelfUpdateEnvelope {
	success: boolean;
	check: SelfUpdateCheck;
	selfUpdateConfigured: boolean;
}

/**
 * How a worker's image can be rolled.
 *
 * socket — cluster-manager created the container and recreates it directly.
 * agent  — a worker on another host, rolled by its own updater-agent.
 * none   — registered by hand with neither, so it cannot be rolled from here.
 */
export type UpdateVia = "socket" | "agent" | "none";

export interface WorkerUpdateRow {
	hostname: string;
	publicIp: string;
	enabled: boolean;
	desiredState: "running" | "stopped";
	updateVia: UpdateVia;
	canUpdate: boolean;
	hasUpdaterAgent: boolean;
	currentApiVersion: string | null;
	imageRef: string | null;
	imageDigest: string | null;
	targetDigest: string | null;
	/** Whether drift was decided by image digest (exact) or app version (approximate). */
	driftSource: "digest" | "version";
	latestImage: string | null;
	updateAvailable: boolean;
}

export interface WorkerUpdatesEnvelope {
	success: boolean;
	workers: WorkerUpdateRow[];
	check: {
		latest: string | null;
		channel: string;
		error: string | null;
		checkedAt: string;
	};
}

interface OperationStartedEnvelope {
	success: boolean;
	operationId: string;
}

export function useSelfUpdateCheck(opts?: { enabled?: boolean }) {
	return useQuery({
		queryKey: ["updates", "self"],
		queryFn: async () =>
			(await unwrap(api.api.updates.self.get())) as SelfUpdateEnvelope,
		staleTime: CHECK_STALE_MS,
		gcTime: 24 * 60 * 60_000,
		retry: 1,
		enabled: opts?.enabled ?? true,
	});
}

export function useWorkerUpdates() {
	return useQuery({
		queryKey: ["updates", "workers"],
		queryFn: async () =>
			(await unwrap(api.api.updates.workers.get())) as WorkerUpdatesEnvelope,
		staleTime: CHECK_STALE_MS,
		retry: 1,
	});
}

/** Invalidate both update checks (the "Check now" affordance). */
export function useRefreshUpdateChecks() {
	const queryClient = useQueryClient();
	return () =>
		queryClient.invalidateQueries({
			queryKey: ["updates"],
			refetchType: "active",
		});
}

/**
 * Trigger the cluster-manager self-update. On success the caller receives
 * the operationId (navigate to the Operation detail to follow the roll).
 */
export function useSelfUpdate(opts?: { onStarted?: (opId: string) => void }) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (vars?: { tag?: string }) =>
			(await unwrap(
				api.api.updates.self.post(vars?.tag ? { tag: vars.tag } : {}),
			)) as OperationStartedEnvelope,
		onSuccess: (data) => {
			toast.success("Cluster manager update started");
			queryClient.invalidateQueries({ queryKey: ["updates", "self"] });
			opts?.onStarted?.(data.operationId);
		},
		onError: (err) =>
			toast.error(
				err instanceof Error ? err.message : "Self-update failed to start",
			),
	});
}

/**
 * Drain-then-roll one worker. On success the caller receives the
 * operationId of the update-worker Operation.
 */
export function useWorkerRoll(opts?: {
	onStarted?: (opId: string, hostname: string) => void;
}) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (vars: { hostname: string; tag?: string }) =>
			(await unwrap(
				api.api.updates
					.workers({ hostname: vars.hostname })
					.post(vars.tag ? { tag: vars.tag } : {}),
			)) as OperationStartedEnvelope,
		onSuccess: (data, vars) => {
			toast.success(`Update started for ${vars.hostname}`);
			queryClient.invalidateQueries({ queryKey: ["updates", "workers"] });
			queryClient.invalidateQueries({ queryKey: ["workers"] });
			opts?.onStarted?.(data.operationId, vars.hostname);
		},
		onError: (err) =>
			toast.error(
				err instanceof Error ? err.message : "Worker update failed to start",
			),
	});
}
